import { useState, useRef, useEffect } from "react";
import { WebVTTParser } from "webvtt-parser";
import SubtitleEditor from "./Components/SubtitleEditor-2";
import { toast } from "react-toastify";
import SubtitleEditorLoader from "./Components/SubtitleEditorLoader";
import { Backdrop, CircularProgress, Box } from '@mui/material';

const AudioPlayer = ({ audioFileUrl, tracks, title }) => {
    const [subtitles, setSubtitles] = useState([]);
    const [selectedSubtitle, setSelectedSubtitle] = useState(null);
    const [vttTree, setVttTree] = useState(null);
    const [currentTime, setCurrentTime] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [replay, setReplay] = useState(false);
    const [replayEndTime, setReplayEndTime] = useState(null);
    const [duration, setDuration] = useState(null);
    const audioRef = useRef(null);
    const subtitleEditorRef = useRef();
    const [isSubtitleLoading, setIsSubtitleLoading] = useState(false);
    const [countdownTime, setCountdownTime] = useState(null);
    const countdownTimerRef = useRef(null);
    const [subtitleStartTime, setSubtitleStartTime] = useState(null);
    const [isSaving, setIsSaving] = useState(false);

    // Fetch subtitles with per-track error handling
    const fetchSubtitles = async () => {
        if (tracks && tracks.length > 0) {
            const subtitleData = await Promise.all(
                tracks.map(async (trackUrl) => {
                    try {
                        const response = await fetch(
                            `${process.env.REACT_APP_API_ENTRYPOINT}/tracks/${trackUrl.split('/').pop()}`,
                            {
                                headers: {
                                    'Authorization': `Bearer ${localStorage.getItem("token")}`
                                }
                            }
                        );
                        if (!response.ok) throw new Error(`Failed to fetch track ${trackUrl}`);
                        const data = await response.json();
                        return {
                            label: data.label,
                            src: data.mediaUrl,
                            language: data.label,
                            id: data.id,
                            startsAt: data.startsAt
                        };
                    } catch (error) {
                        console.error(`Error fetching track ${trackUrl}:`, error);
                        return null;
                    }
                })
            );
            return subtitleData.filter(sub => sub !== null);
        }
        return [];
    };

    // Load subtitles and initialize countdown on mount or when tracks/duration change
    useEffect(() => {
        const loadSubtitles = async () => {
            const subtitleData = await fetchSubtitles();
            setSubtitles(subtitleData);
            if (subtitleData.length > 0) {
                // Prefer Arabic subtitle, otherwise take the first one
                const initialSubtitle = subtitleData.find(sub => sub.language === "ar") || subtitleData[0];
                setSelectedSubtitle(initialSubtitle);
                if (initialSubtitle.startsAt) {
                    setSubtitleStartTime(initialSubtitle.startsAt);
                }
                if (!initialSubtitle.src && duration) {
                    startCountdown(initialSubtitle.startsAt);
                } else if (initialSubtitle.src) {
                    await handleSubtitleSelect(initialSubtitle);
                }
            }
        };
        loadSubtitles();
    }, [tracks, duration]);

    // Audio event listeners
    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const handlePlay = () => setIsPlaying(true);
        const handlePause = () => setIsPlaying(false);
        const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
        const handleLoadedMetadata = () => setDuration(audio.duration);

        audio.addEventListener("play", handlePlay);
        audio.addEventListener("pause", handlePause);
        audio.addEventListener("timeupdate", handleTimeUpdate);
        audio.addEventListener("loadedmetadata", handleLoadedMetadata);

        setIsPlaying(!audio.paused);

        return () => {
            audio.removeEventListener("play", handlePlay);
            audio.removeEventListener("pause", handlePause);
            audio.removeEventListener("timeupdate", handleTimeUpdate);
            audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
        };
    }, [audioFileUrl]);

    // Cleanup countdown timer on unmount
    useEffect(() => {
        return () => {
            if (countdownTimerRef.current) {
                clearInterval(countdownTimerRef.current);
            }
        };
    }, []);

    // Calculate remaining time based on startsAt, audio duration, and current time
    const calculateRemainingTime = (startsAt) => {
        if (!startsAt || !duration) {
            return null; // Return null if data is incomplete
        }

        let startDate;
        if (startsAt.includes('T')) {
            startDate = new Date(startsAt); // ISO format
        } else {
            startDate = new Date(startsAt.replace(' ', 'T') + 'Z'); // Space-separated format
        }

        const readyDate = new Date(startDate.getTime() + (duration * 1000));
        const currentDate = new Date();
        const remainingSeconds = Math.max(0, Math.floor((readyDate - currentDate) / 1000));

        return remainingSeconds;
    };

    // Start countdown timer with calculated remaining time
    const startCountdown = (startsAt) => {
        if (countdownTimerRef.current) {
            clearInterval(countdownTimerRef.current);
        }

        const remainingTime = calculateRemainingTime(startsAt);
        if (remainingTime === null) {
            setCountdownTime(null);
            return;
        }
        setCountdownTime(remainingTime);

        countdownTimerRef.current = setInterval(() => {
            setCountdownTime(prevTime => {
                if (prevTime <= 1) {
                    clearInterval(countdownTimerRef.current);
                    return 0;
                }
                return prevTime - 1;
            });
        }, 1000);
    };

    // Format countdown time as MM:SS
    const formatCountdownTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    const fetchSubtitleFile = async (subtitleUrl) => {
        try {
            const response = await fetch(subtitleUrl, { cache: "no-store" });
            if (!response.ok) throw new Error("Failed to load subtitle file");
            const text = await response.text();
            parseVTT(text);
        } catch (error) {
            console.error("Error fetching subtitle file:", error);
        }
    };

    const parseVTT = (vttText) => {
        const parser = new WebVTTParser();
        const parsedVTT = parser.parse(vttText, "metadata");
        if (parsedVTT.errors.length > 0 && parsedVTT.cues.length === 0) {
            console.error("VTT Parsing Errors:", parsedVTT.errors);
            return;
        }
        parsedVTT.cues = parsedVTT.cues.map((cue, index) => {
            const startDate = new Date(cue.startTime * 1000);
            const endDate = new Date(cue.endTime * 1000);
            return {
                ...cue,
                text: cue.text,
                startTime: cue.startTime,
                endTime: cue.endTime,
                startHours: padZero(startDate.getUTCHours()),
                startMinutes: padZero(startDate.getUTCMinutes()),
                startSeconds: padZero(startDate.getUTCSeconds()),
                startMilliseconds: padZero(startDate.getUTCMilliseconds(), { length: 3 }),
                endHours: padZero(endDate.getUTCHours()),
                endMinutes: padZero(endDate.getUTCMinutes()),
                endSeconds: padZero(endDate.getUTCSeconds()),
                endMilliseconds: padZero(endDate.getUTCMilliseconds(), { length: 3 }),
                index,
            };
        });
        setVttTree(parsedVTT);
    };

    const padZero = (number, config = {}) => {
        const { length = 2 } = config;
        const numberString = number.toString();
        if (numberString.length < length) {
            const padLength = length - numberString.length;
            let zeroString = "";
            for (let i = 0; i < padLength; i++) {
                zeroString = `0${zeroString}`;
            }
            return `${zeroString}${numberString}`;
        }
        return numberString;
    };

    const handleSubtitleSelect = async (subtitle) => {
        setIsSubtitleLoading(true);
        setSelectedSubtitle(subtitle);
        setVttTree(null);

        if (subtitle.startsAt) {
            setSubtitleStartTime(subtitle.startsAt);
        }

        if (subtitle.src) {
            if (countdownTimerRef.current) {
                clearInterval(countdownTimerRef.current);
                setCountdownTime(null);
            }
            await fetchSubtitleFile(subtitle.src);
        } else {
            startCountdown(subtitle.startsAt);
            const updatedSubtitles = await fetchSubtitles();
            setSubtitles(updatedSubtitles);
            const updatedSubtitle = updatedSubtitles.find(sub => sub.id === subtitle.id);
            if (updatedSubtitle?.src) {
                clearInterval(countdownTimerRef.current);
                setCountdownTime(null);
                await fetchSubtitleFile(updatedSubtitle.src);
                setSelectedSubtitle(updatedSubtitle);
            }
        }
        setIsSubtitleLoading(false);
    };

    // Refetch subtitles when countdown reaches 0
    useEffect(() => {
        if (countdownTime === 0 && selectedSubtitle && !selectedSubtitle.src) {
            const refetchSubtitles = async () => {
                const updatedSubtitles = await fetchSubtitles();
                setSubtitles(updatedSubtitles);
                const updatedSubtitle = updatedSubtitles.find(sub => sub.id === selectedSubtitle.id);
                if (updatedSubtitle?.src) {
                    setSelectedSubtitle(updatedSubtitle);
                    await fetchSubtitleFile(updatedSubtitle.src);
                }
            };
            refetchSubtitles();
        }
    }, [countdownTime, selectedSubtitle]);

    // Playback controls
    const handleSeekAndPlay = (time) => {
        if (audioRef.current) {
            audioRef.current.currentTime = time;
            audioRef.current.play();
        }
    };

    const handlePause = () => {
        if (audioRef.current) {
            audioRef.current.pause();
        }
    };

    const handleReplay = async (startTime, endTime) => {
        if (audioRef.current) {
            try {
                setReplay(true);
                setReplayEndTime(endTime);
                audioRef.current.currentTime = startTime;
                setCurrentTime(audioRef.current.currentTime);
                await audioRef.current.play();
            } catch (error) {
                console.error(error);
                setReplay(false);
                setReplayEndTime(null);
            }
        }
    };

    useEffect(() => {
        if (replay && replayEndTime !== null && currentTime >= replayEndTime - 0.1) {
            audioRef.current.pause();
            audioRef.current.currentTime = replayEndTime - 0.1;
            setReplay(false);
            setReplayEndTime(null);
        }
    }, [currentTime, replay, replayEndTime]);

    const generateVttString = (list) => {
        const formatTime = (hours, minutes, seconds, milliseconds) => {
            return `${padZero(hours)}:${padZero(minutes)}:${padZero(seconds)}.${padZero(milliseconds, { length: 3 })}`;
        };
        return (
            "WEBVTT\n\n" +
            list
                .map((cue) => {
                    const start = formatTime(
                        cue.startHours,
                        cue.startMinutes,
                        cue.startSeconds,
                        cue.startMilliseconds
                    );
                    const end = formatTime(
                        cue.endHours,
                        cue.endMinutes,
                        cue.endSeconds,
                        cue.endMilliseconds
                    );
                    return `${start} --> ${end}\n${cue.text}\n`;
                })
                .join("\n")
        );
    };

    const handleSaveSubtitles = async (data) => {
        setIsSaving(true);
        const toastId = toast.loading("Saving subtitles...");
        try {
            const vttString = generateVttString(data.list);
            const blob = new Blob([vttString], { type: "text/vtt" });
            const formData = new FormData();
            formData.append("file", blob, selectedSubtitle.src.split('/').pop());

            const response = await fetch(
                `${process.env.REACT_APP_API_ENTRYPOINT}/tracks/${selectedSubtitle.id}/update`,
                {
                    method: "POST",
                    body: formData,
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem("token")}`
                    }
                }
            );

            if (response.ok) {
                toast.update(toastId, {
                    render: "Subtitles saved successfully!",
                    type: "success",
                    isLoading: false,
                    autoClose: 5000,
                });
            } else {
                throw new Error("Upload failed");
            }
        } catch (error) {
            console.error("Error:", error);
            toast.update(toastId, {
                render: "Failed to save subtitles. Please try again.",
                type: "error",
                isLoading: false,
                autoClose: 5000,
            });
        } finally {
            setIsSaving(false);
        }
    };

    const handleDownload = () => {
        if (subtitleEditorRef.current) {
            const list = subtitleEditorRef.current.getCurrentData();
            const vttString = generateVttString(list);
            const blob = new Blob([vttString], { type: "text/vtt" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${selectedSubtitle.src.split('/').pop()}`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        }
    };

    const handleSaveClick = () => {
        if (subtitleEditorRef.current) {
            subtitleEditorRef.current.save();
        }
    };

    return (
        <>
            <div className="audio-player-container">
                <div className="audio-content">
                    <div className="audio-title">
                        <h3>{title}</h3>
                    </div>
                    <div className="audio-layout">
                        <div className="audio-section">
                            <div className="audio-wrapper">
                                <audio
                                    ref={audioRef}
                                    src={audioFileUrl}
                                    controls
                                    className="audio-element"
                                />
                            </div>
                            {subtitles.length > 0 && (
                                <div className="subtitles-selector">
                                    <h3>Available Subtitles</h3>
                                    <div className="subtitle-buttons">
                                        {subtitles.map((subtitle) => (
                                            <button
                                                key={subtitle.id}
                                                onClick={() => handleSubtitleSelect(subtitle)}
                                                className={`subtitle-button ${selectedSubtitle?.id === subtitle.id ? "active" : ""}`}
                                            >
                                                {subtitle.language}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {selectedSubtitle && vttTree && (
                                <div>
                                    <button
                                        className="save-button"
                                        onClick={handleSaveClick}
                                        disabled={!vttTree}
                                    >
                                        Save Changes
                                    </button>
                                    <button
                                        className="download-button"
                                        onClick={handleDownload}
                                        disabled={!vttTree}
                                    >
                                        Download Subtitle
                                    </button>
                                </div>
                            )}
                        </div>
                        <div className="subtitles-section">
                            {(!tracks || tracks.length === 0) ? (
                                <p className="not-found-message">The subtitle is not available yet.</p>
                            ) : selectedSubtitle ? (
                                <div className="subtitle-editor-container">
                                    <div className="subtitle-editor-wrapper">
                                        {isSubtitleLoading ? (
                                            <SubtitleEditorLoader />
                                        ) : vttTree ? (
                                            <SubtitleEditor
                                                ref={subtitleEditorRef}
                                                onSubmit={handleSaveSubtitles}
                                                list={vttTree.cues}
                                                onSeekAndPlay={handleSeekAndPlay}
                                                onPause={handlePause}
                                                onReplay={handleReplay}
                                                currentTime={currentTime}
                                                isPlaying={isPlaying}
                                                duration={duration}
                                            />
                                        ) : (
                                            <p className="not-found-message">
                                                {countdownTime !== null
                                                    ? `Subtitle will be available in ${formatCountdownTime(countdownTime)}`
                                                    : ""}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    </div>
                </div>
            </div>

            <Backdrop
                sx={{
                    zIndex: (theme) => theme.zIndex.modal + 1,
                    color: '#fff',
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
                open={isSaving}
            >
                <CircularProgress color="inherit" />
                <Box sx={{ mt: 2, fontSize: '1.2rem' }}>Saving subtitles, please wait...</Box>
            </Backdrop>
        </>
    );
};

export default AudioPlayer;
